<template>
    <div class="dt-row-wrap">
        <div class="dt-row" :class="[rowClass, {'dt-row--selected': isSelected, 'dt-row--expanded': isExpanded, 'dt-row--readonly': row._readonly}]" @click="onRowClick">
            <div v-if="selectable" class="dt-cell dt-cell--checkbox" @click.stop="onToggleSelect">
                <span v-if="row._selectable !== false" class="dt-checkbox" :class="{'dt-checkbox--checked': isSelected}">
                    <svg v-if="isSelected" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
            </div>
            <div v-if="expandable" class="dt-cell dt-cell--expand" @click.stop="onToggleExpand">
                <button v-if="row._expandable !== false" class="dt-expand-btn" :class="{'dt-expand-btn--open': isExpanded}">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
            </div>
            <div v-for="col in columns" :key="col.key"
                 class="dt-cell"
                 :class="[col.cellClass, 'dt-align-' + col.align]"
                 :style="cellStyle(col)"
                 @dblclick="onCellDblClick(col)">
                <template v-if="isEditing(col.key)">
                    <input ref="editInput" class="dt-edit-input" :value="row[col.key]"
                           @keydown.enter="onEditCommit($event, col.key)"
                           @keydown.escape.stop="onEditCancel"
                           @blur="onEditCommit($event, col.key)"/>
                </template>
                <template v-else-if="col.render">
                    <span class="dt-cell-render" v-html="col.render(row[col.key], row)"></span>
                </template>
                <template v-else>
                    <span class="dt-cell-value" :style="cellValueStyle(col)" v-html="formatValue(col, row[col.key])"></span>
                </template>
            </div>
            <div v-if="hasActions && visibleActions.length" class="dt-cell dt-cell--actions">
                <template v-if="actionStyle === 'inline'">
                    <button v-for="act in visibleActions" :key="act.key" class="dt-action-btn" :class="{'dt-action-btn--danger': act.danger}" :title="act.label" @click.stop="onAction(act.key)">
                        <span v-if="act.icon" v-html="act.icon"></span>
                        <span v-else class="dt-action-label">{{ act.label }}</span>
                    </button>
                </template>
                <template v-else>
                    <div class="dt-action-wrap">
                        <button ref="actionTrigger" class="dt-action-trigger" @click.stop="onDropdownToggle">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                        </button>
                        <div v-if="dropdownOpen" class="dt-action-dropdown" :style="dropdownStyle" @click.stop>
                            <button v-for="act in visibleActions" :key="act.key" class="dt-action-dropdown-item" :class="{'dt-action-dropdown-item--danger': act.danger}" @click.stop="onAction(act.key)">
                                <span v-if="act.icon" class="dt-action-dropdown-icon" v-html="act.icon"></span>
                                <span>{{ act.label }}</span>
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
        <div v-if="isExpanded && renderExpand" class="dt-row-expand">
            <div class="dt-row-expand-content" v-html="expandContent"></div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DataTableRow',
    props: {
        row: Object,
        columns: Array,
        rowKey: String,
        selectable: Boolean,
        expandable: Boolean,
        hasActions: Boolean,
        actions: Array,
        actionStyle: String,
        isSelected: Boolean,
        isExpanded: Boolean,
        editingCell: Object,
        editable: Boolean,
        renderExpand: Function,
        columnWidths: Object,
        isCompact: Boolean,
    },
    emits: ['select', 'expand', 'action', 'row-click', 'edit-start', 'edit-commit', 'edit-cancel'],
    data() {
        return {
            dropdownOpen: false,
            dropdownStyle: null,
        }
    },
    created() {
        this._closeHandlers = null
    },
    computed: {
        rowClass() {
            return this.row._rowClass || ''
        },
        visibleActions() {
            if (!this.actions) return []
            if (Array.isArray(this.row._actions)) {
                return this.actions.filter(a => this.row._actions.includes(a.key))
            }
            return this.actions
        },
        expandContent() {
            if (this.renderExpand) return this.renderExpand(this.row)
            return ''
        },
    },
    methods: {
        cellStyle(col) {
            const w = this.columnWidths[col.key] || col.width
            if (w) return { width: w + 'px', minWidth: col.minWidth + 'px', flexGrow: 0, flexShrink: 0, flexBasis: w + 'px' }
            return { flex: col.flex ?? 1, minWidth: col.minWidth + 'px' }
        },
        cellValueStyle(col) {
            const c = col.color
            if (!c) return null
            const color = typeof c === 'function' ? c(this.row[col.key], this.row) : c
            return color ? { color } : null
        },
        formatValue(col, value) {
            if (value == null) return ''
            const fmt = col.format
            if (typeof fmt === 'function') return fmt(value, this.row)
            if (fmt === 'date') return this.formatDate(value)
            if (fmt === 'currency') return this.formatCurrency(value)
            if (fmt === 'number') return this.formatNumber(value)
            if (fmt === 'percent') return value + '%'
            if (fmt === 'badge') {
                const color = typeof col.color === 'function' ? col.color(value, this.row) : (col.color || 'var(--dt-accent)')
                return '<span class="dt-badge" style="--badge-color:' + color + '">' + value + '</span>'
            }
            return String(value)
        },
        formatDate(val) {
            try {
                const d = new Date(val)
                const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara']
                return d.getDate() + ' ' + months[d.getMonth()] + ' ' + d.getFullYear()
            } catch { return val }
        },
        formatCurrency(val) {
            const n = Number(val)
            if (isNaN(n)) return val
            return n.toLocaleString('tr-TR') + '₺'
        },
        formatNumber(val) {
            const n = Number(val)
            if (isNaN(n)) return val
            return n.toLocaleString('tr-TR')
        },
        isEditing(colKey) {
            if (!this.editingCell) return false
            return this.editingCell.rowKey === this.row[this.rowKey] && this.editingCell.columnKey === colKey
        },
        onCellDblClick(col) {
            if (!this.editable || !col.editable || this.row._readonly) return
            if (this.isCompact) return
            this.$emit('edit-start', this.row[this.rowKey], col.key)
            this.$nextTick(() => {
                const input = this.$refs.editInput
                if (Array.isArray(input)) input[0]?.focus()
                else input?.focus()
            })
        },
        onEditCommit(e, colKey) {
            this.$emit('edit-commit', this.row[this.rowKey], colKey, e.target.value)
        },
        onEditCancel() {
            this.$emit('edit-cancel')
        },
        onToggleSelect() {
            if (this.row._selectable === false) return
            this.$emit('select', this.row[this.rowKey])
        },
        onToggleExpand() {
            if (this.row._expandable === false) return
            this.$emit('expand', this.row[this.rowKey])
        },
        onRowClick() {
            this.$emit('row-click', this.row)
        },
        onAction(key) {
            this._dismissDropdown()
            this.$emit('action', key, this.row)
        },
        _getDropdownPosition() {
            const btn = this.$refs.actionTrigger
            if (!btn) return {}
            const rect = btn.getBoundingClientRect()
            const cb = btn.closest('.panel-content--transparent')
            const cr = cb ? cb.getBoundingClientRect() : { top: 0, right: window.innerWidth, bottom: window.innerHeight }
            const spaceBelow = window.innerHeight - rect.bottom
            const style = {
                position: 'fixed',
                right: (cr.right - rect.right) + 'px',
                left: 'auto',
                minWidth: '160px',
                zIndex: 9999,
            }
            if (spaceBelow < 200 && rect.top > spaceBelow) {
                style.bottom = (cr.bottom - rect.top + 4) + 'px'
                style.top = 'auto'
            } else {
                style.top = (rect.bottom + 4 - cr.top) + 'px'
                style.bottom = 'auto'
            }
            return style
        },
        onDropdownToggle() {
            if (this.dropdownOpen) {
                this._dismissDropdown()
                return
            }
            this.dropdownStyle = this._getDropdownPosition()
            this.dropdownOpen = true
            const dismiss = () => this._dismissDropdown()
            const onMousedown = (e) => {
                if (!this.$el.contains(e.target)) dismiss()
            }
            this._closeHandlers = { onMousedown, dismiss }
            setTimeout(() => {
                document.addEventListener('mousedown', onMousedown)
                document.addEventListener('scroll', dismiss, true)
            }, 0)
        },
        _dismissDropdown() {
            this.dropdownOpen = false
            this.dropdownStyle = null
            if (this._closeHandlers) {
                document.removeEventListener('mousedown', this._closeHandlers.onMousedown)
                document.removeEventListener('scroll', this._closeHandlers.dismiss, true)
                this._closeHandlers = null
            }
        },
    },
    beforeUnmount() {
        this._dismissDropdown()
    },
}
</script>
